import { useState, useEffect } from 'react'
import { useAccessCodeStore } from '@/store/accessCode'
import toast from 'react-hot-toast'

export function AccessCodeModal() {
  const [code, setCode] = useState('')
  const { isLoading, error, verifyCode } = useAccessCodeStore()

  // 防止 ESC 键关闭Modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
      }
    }
    
    // 使用捕获阶段，确保能拦截 ESC 键
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!code || code.length !== 4) {
      toast.error('请输入4位数字验证码')
      return
    }
    
    if (!/^\d+$/.test(code)) {
      toast.error('验证码只能是数字')
      return
    }

    try {
      await verifyCode(code)
      toast.success('验证成功')
    } catch (err) {
      // 错误已由 store 处理，这里只需要展示
      toast.error('验证码错误，请重试')
    }
  }

  // 输入框焦点时增加玻璃效果
  const [focused, setFocused] = useState(false)

  return (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-slate-900/40 via-slate-800/40 to-slate-900/40 backdrop-blur-md flex items-center justify-center z-50"
      style={{
        background: 'radial-gradient(circle at 50% 50%, rgba(15, 23, 42, 0.5) 0%, rgba(15, 23, 42, 0.8) 100%)'
      }}
    >
      {/* 玻璃拟态卡片 */}
      <div 
        className="relative w-96 rounded-2xl overflow-hidden shadow-2xl"
        style={{
          background: 'rgba(30, 41, 59, 0.7)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {/* 背景光晕效果 */}
        <div 
          className="absolute -top-20 -left-20 w-40 h-40 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)'
          }}
        />
        <div 
          className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)'
          }}
        />

        <div className="relative p-8 space-y-6">
          {/* 标题和描述 */}
          <div className="text-center space-y-3">
            <div className="text-4xl mb-4 opacity-60">🔐</div>
            <h2 
              className="text-2xl font-bold"
              style={{
                background: 'linear-gradient(135deg, #F8FAFC 0%, #94A3B8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              建设中
            </h2>
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              请输入验证码继续浏览
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 验证码输入框 */}
            <div className="relative">
              <input
                type="text"
                maxLength={4}
                placeholder="0000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                className="w-full px-6 py-4 text-center text-3xl font-bold tracking-[0.3em] font-mono transition-all duration-300 rounded-xl outline-none"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: focused 
                    ? '1.5px solid rgba(59, 130, 246, 0.5)' 
                    : '1.5px solid rgba(255, 255, 255, 0.1)',
                  color: '#F8FAFC',
                  backdropFilter: 'blur(10px)',
                  boxShadow: focused
                    ? 'inset 0 2px 8px rgba(59, 130, 246, 0.1), 0 0 20px rgba(59, 130, 246, 0.2)'
                    : 'inset 0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                disabled={isLoading}
                autoFocus
              />
              {/* 光线效果 */}
              {focused && (
                <div 
                  className="absolute inset-0 rounded-xl pointer-events-none"
                  style={{
                    background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                  }}
                />
              )}
            </div>
            
            {/* 错误提示 */}
            {error && (
              <p className="text-sm text-center" style={{ color: '#F97316' }}>
                ❌ {error}
              </p>
            )}
            
            {/* 验证按钮 */}
            <button
              type="submit"
              disabled={isLoading || code.length !== 4}
              className="w-full py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95"
              style={{
                background: code.length === 4 && !isLoading
                  ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)'
                  : 'rgba(255, 255, 255, 0.05)',
                color: code.length === 4 && !isLoading ? '#F8FAFC' : '#64748B',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: code.length === 4 && !isLoading
                  ? '0 0 30px rgba(59, 130, 246, 0.4), 0 0 60px rgba(139, 92, 246, 0.2)'
                  : 'none',
                cursor: code.length === 4 && !isLoading ? 'pointer' : 'not-allowed'
              }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span 
                    className="inline-block w-4 h-4 rounded-full"
                    style={{
                      background: 'linear-gradient(90deg, #3B82F6, #8B5CF6)',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  验证中...
                </span>
              ) : (
                '验证访问 →'
              )}
            </button>
          </form>

          {/* 底部提示 */}
          <p className="text-center text-xs" style={{ color: '#64748B' }}>
            输入 4 位数字验证码
          </p>
        </div>
      </div>

      {/* CSS 动画 */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        input::placeholder {
          color: rgba(148, 163, 184, 0.4) !important;
        }
      `}</style>
    </div>
  )
}
